<template>
  <div
    class="VuePagination flex justify-center md:justify-end"
    :class="props.theme.wrapper"
  >
    <nav :class="props.theme.nav">
      <ul
        v-if="props.showPagination"
        class="flex flex-row gap-1 md:gap-2"
        :class="props.theme.list"
      >
        <li
          v-if="props.hasEdgeNav"
          :class="props.theme.firstPage"
          @click="props.setFirstPage"
        >
          <a v-bind="{ ...props.aProps, ...props.firstPageProps }">{{
            props.texts.first
          }}</a>
        </li>

        <li
          v-if="props.hasChunksNav"
          :class="props.theme.prevChunk"
          @click="props.setPrevChunk"
        >
          <NuxtLink v-bind="{ ...props.aProps, ...props.prevChunkProps }">
            <IconChevronsDown class="rotate-90 mt-1 text-black" />
          </NuxtLink>
        </li>

        <li class="link-prev" @click="props.setPrevPage">
          <a v-bind="{ ...props.aProps, ...props.prevProps }">
            <IconChevronLeft />
          </a>
        </li>

        <template
          v-for="page in getVisiblePages(props.page, props.totalPages, 1)"
          :key="page"
        >
          <li v-if="page === '...'" class="page-item page-item--ellipsis">
            {{ page }}
          </li>
          <li
            v-else
            class="page-item"
            :class="props.page === page ? 'page-item--current' : ''"
            v-on="page < maximumAvailablePage ? props.pageEvents(page) : null"
          >
            <component
              :is="page <= maximumAvailablePage ? NuxtLink : 'span'"
              v-bind="props.aProps"
              :to="
                fullLocalPath(localePath({ query: { ...$route.query, page } }))
              "
              :class="props.theme.link"
              @click.prevent
            >
              {{ page }}
            </component>
          </li>
        </template>

        <li
          v-show="props.page < maximumAvailablePage"
          class="link-next"
          @click="props.setNextPage"
        >
          <a v-bind="{ ...props.aProps, ...props.nextProps }">
            <IconChevronLeft class="rotate-180" />
          </a>
        </li>

        <li
          v-if="props.hasChunksNav && page < maximumAvailablePage"
          :class="props.theme.nextChunk"
          @click="props.setNextChunk"
        >
          <a v-bind="{ ...props.aProps, ...props.nextChunkProps }">
            <IconChevronsDown class="-rotate-90 mt-1 text-black" />
          </a>
        </li>

        <li
          v-if="props.hasEdgeNav"
          :class="props.theme.lastPage"
          @click="props.setLastPage"
        >
          <a v-bind="{ ...props.aProps, ...props.lastPageProps }">{{
            props.texts.last
          }}</a>
        </li>
      </ul>
    </nav>
  </div>
</template>

<script setup>
import { NuxtLink } from '#components'
import IconChevronLeft from '~/assets/icons/chevron-left.svg?component'
import IconChevronsDown from '~/components/icons/IconChevronsDown'

const $localePath = useLocalePath()
const localePath = $localePath

const fullLocalPath = useFullLocalizedUrl(true)
// who name it pp xD
const pp = defineProps(['props'])

const maximumAvailablePage = computed(() => {
  const maximumVisibleEvents = 1000 // to adjust if required
  const itemsPerPage = pp.props.perPage

  return Math.ceil(maximumVisibleEvents / itemsPerPage)
})

const getVisiblePages = (
  currentPage,
  totalPages,
  showPagesAroundCurrent = 3
) => {
  // Handle invalid inputs
  if (currentPage < 1 || currentPage > totalPages) {
    return []
  }

  const pages = new Set() // Using Set to avoid duplicates

  // Always add first and last pages
  pages.add(1)
  pages.add(totalPages)

  // Calculate the range around current page
  const rangeStart = Math.max(2, currentPage - showPagesAroundCurrent)
  const rangeEnd = Math.min(
    totalPages - 1,
    currentPage + showPagesAroundCurrent
  )

  // Add current page and pages around it
  for (let i = rangeStart; i <= rangeEnd; i++) {
    pages.add(i)
  }

  // Convert Set to sorted array
  const visiblePages = [...pages].sort((a, b) => a - b)

  // Add ellipsis markers
  const result = []
  for (let i = 0; i < visiblePages.length; i++) {
    if (i > 0 && visiblePages[i] - visiblePages[i - 1] > 1) {
      result.push('...')
    }
    result.push(visiblePages[i])
  }

  return result
}
</script>

<style lang="postcss" scoped>
.link-next,
.link-prev {
  @apply flex h-10 items-center justify-center cursor-pointer;

  :deep(svg path) {
    @apply duration-300 ease-in-out;
  }

  &:hover {
    :deep(svg path) {
      fill: #374151;
    }
  }
}
.page-item {
  @apply flex w-10 h-10;

  & > span,
  & > a {
    @apply w-full h-full flex items-center justify-center text-center rounded-lg ease-in-out duration-300;
    @apply text-gray-400 text-sm md:text-base font-normal cursor-pointer;
  }

  & > span {
    @apply cursor-auto;
  }

  &:not(&--current) > a:hover {
    @apply bg-gray-100 text-gray-700;
  }

  &--current > a {
    @apply bg-teal-900 text-white;
  }

  &--ellipsis {
    @apply !w-auto md:w-10 items-center justify-center text-center text-xs text-gray-400;
  }
}
</style>
